import { TCompanyStore } from 'src/app/Company.store';

type TAuthenticationUrl = TCompanyStore['data']['authentication_urls'][0];
type TProvider = TAuthenticationUrl['provider'];

/**
 * Provider values that mean "Suomi.fi / Finnish Trust Network".
 *
 * **A provider does not tell you the tenant's country.** "Finnish Trust
 * Network" is the *scheme's* name, not a statement about whoever uses it. The
 * `hra-portal` tenant is Norwegian (`country: 'NO'`, `default_language: 'NO'`,
 * and BankID with a `urn:grn:authn:no:…` acr-value on production) yet its
 * preview-2 config emits `provider: 'suomifi'`. Never infer nationality,
 * language, or formatting from a provider value — read `company.country` /
 * `company.default_language` for that. Providers from different countries can
 * and do coexist on one tenant.
 *
 * The backend emits `'suomifi'` — verified against a live tenant response,
 * which carries `{ acr_values: 'suomifi', provider: 'suomifi', url:
 * 'https://<platform-host>/suomifi/saml/login' }`.
 *
 * `'ftn'` is accepted too, and only for that reason: the original frontend
 * scaffold was written before any tenant was provisioned and guessed that
 * spelling. Nothing observed emits it, but accepting both costs one array entry
 * and removes any chance of a tenant silently falling through to the NemID
 * bucket (which is what the `'ftn'`-only check did against the real payload).
 */
const FTN_PROVIDERS: readonly TProvider[] = ['suomifi', 'ftn'];

/** True when this `authentication_urls` provider is Suomi.fi / FTN. */
export const isFinnishTrustNetworkProvider = (
  provider: TProvider | string
): boolean => FTN_PROVIDERS.includes(provider as TProvider);

/** The Suomi.fi entries in a company's `authentication_urls`, in backend order. */
export const selectFtnUrls = (
  authenticationUrls: readonly TAuthenticationUrl[] | undefined | null
): TAuthenticationUrl[] =>
  (authenticationUrls ?? []).filter((u) =>
    isFinnishTrustNetworkProvider(u.provider)
  );

/**
 * Provider values that mean "ID-porten" — Digdir's Norwegian public-sector eID
 * gateway (Avfall Sør).
 *
 * The wire value is `'idporten'`: the backend's
 * `FastAuthenticationUrlProvider.IDPORTEN = "idporten"`
 * (`crm/api/v1/schemes/company.py`), emitted iff the company has an
 * `IdPortenApplication` row — the row IS the activation, the same one-part gate
 * Criipto has.
 *
 * **One provider value, not one per eID.** ID-porten itself offers BankID, Buypass,
 * Commfides and MinID behind its own picker; the portal shows one
 * "Logg inn med ID-porten" button and never names those vendors.
 */
const IDPORTEN_PROVIDER: TProvider = 'idporten';

/** True when this `authentication_urls` provider is ID-porten. */
export const isIdPortenProvider = (provider: TProvider | string): boolean =>
  provider === IDPORTEN_PROVIDER;

/** The ID-porten entries in a company's `authentication_urls`, in backend order. */
export const selectIdPortenUrls = (
  authenticationUrls: readonly TAuthenticationUrl[] | undefined | null
): TAuthenticationUrl[] =>
  (authenticationUrls ?? []).filter((u) => isIdPortenProvider(u.provider));
